import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

export default function HomePage() {
  const { t } = useLanguage();
  useScrollAnimation();

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById('navbar');
      if (navbar) {
        navbar.style.boxShadow = window.scrollY > 50 
          ? '0 4px 20px rgba(0,0,0,0.1)' 
          : '0 4px 6px rgba(0,0,0,0.1)';
      }
      const scrolled = window.pageYOffset;
      const hero = document.querySelector('.hero');
      if (hero) {
        hero.style.backgroundPositionY = scrolled * 0.5 + 'px';
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const galleryItems = [
    { icon: '👔', title: 'gal_suit', desc: 'gal_suit_desc' },
    { icon: '👗', title: 'gal_dress', desc: 'gal_dress_desc' },
    { icon: '🧥', title: 'gal_leather', desc: 'gal_leather_desc' },
    { icon: '👖', title: 'gal_denim', desc: 'gal_denim_desc' },
    { icon: '👘', title: 'gal_traditional', desc: 'gal_traditional_desc' },
    { icon: '🧵', title: 'gal_embroidery', desc: 'gal_embroidery_desc' },
  ];

  const testimonials = [
    { text: 'review_1', initials: 'A', name: 'Aykhan Imanov' },
    { text: 'review_2', initials: 'M', name: 'Manuel Magnier' },
    { text: 'review_3', initials: 'T', name: 'Thomas Jamne' },
  ];

  const locations = [
    { name: 'Victoria Stil - Los Montesinos', city: 'Los Montesinos, Alicante', address: '📍 Calle Nuestra Señora Virgen del Carmen, 8<br>03187 Los Montesinos, España', map: 'https://maps.app.goo.gl/uVtr9hy5C7CDjd389' },
    { name: 'Victoria Stil - Guardamar', city: 'Guardamar del Segura, Alicante', address: '📍 Av. del País Valencià, 48<br>03140 Guardamar del Segura, Alicante, España', map: 'https://maps.app.goo.gl/aYBtK1e8461YJNpF9' },
    { name: 'Victoria Stil - Torrevieja', city: 'Torrevieja, Alicante', address: '📍 C. Caballero de Rodas, 70<br>03182 Torrevieja, Alicante, España', map: 'https://maps.app.goo.gl/Fq6wzGtRnBwgdMYZ7' },
  ];

  return (
    <>
      <section className="hero" id="home" style={{ background: "url('background.png') center/cover no-repeat" }}>
        <div className="hero-content">
          <h1>{t('hero_title')}</h1>
          <p>{t('hero_subtitle')}</p>
          <p className="hero-reviews">{t('hero_reviews')}</p>
          <p className="hero-trust">{t('hero_trust')}</p>
          <a href="https://wa.me/34611228437" target="_blank" rel="noopener noreferrer" className="btn-get-started">
            {t('btn_get_started')}
          </a>
          <p className="hero-subline">{t('hero_subline')}</p>
        </div>
      </section>

      <section className="gallery" id="gallery">
        <div className="container">
          <div className="section-header fade-in">
            <h2>{t('gallery_title')}</h2>
            <div className="divider"></div>
            <p>{t('gallery_subtitle')}</p>
          </div>
          <div className="gallery-grid">
            {galleryItems.map((item, i) => (
              <div className="gallery-item fade-in" key={i}>
                <span style={{ fontSize: '3rem' }}>{item.icon}</span>
                <div className="gallery-overlay">
                  <h4>{t(item.title)}</h4>
                  <p>{t(item.desc)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="testimonials" id="testimonials">
        <div className="container">
          <div className="section-header fade-in">
            <h2>{t('reviews_title')}</h2>
            <div className="divider"></div>
            <p>{t('reviews_subtitle')}</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((review, i) => (
              <div className="testimonial-card fade-in" key={i}>
                <p className="testimonial-text">{t(review.text)}</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{review.initials}</div>
                  <div className="author-info">
                    <h4>{review.name}</h4>
                    <div className="stars">★★★★★</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="locations" id="locations">
        <div className="container">
          <div className="section-header fade-in">
            <h2>{t('locations_title')}</h2>
            <div className="divider"></div>
            <p>{t('locations_subtitle')}</p>
          </div>
          <div className="locations-grid">
            {locations.map((loc, i) => (
              <div className="location-card fade-in" key={i}>
                <h3>{loc.name}</h3>
                <span className="city">{loc.city}</span>
                <p dangerouslySetInnerHTML={{ __html: loc.address }}></p>
                <a href={loc.map} target="_blank" rel="noopener noreferrer">{t('view_map')}</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        .hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: rgb(0, 0, 0);
          padding: 2rem;
          position: relative;
          overflow: hidden;
        }
        .hero::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(236, 238, 243, 0.65);
          z-index: 1;
        }
        .hero-content {
          position: relative;
          z-index: 2;
          max-width: 900px;
        }
        .hero h1 {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 3rem;
          margin-bottom: 1rem;
          animation: fadeInUp 1s ease;
          font-weight: 600;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }
        .hero p {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 1.3rem;
          margin-bottom: 2rem;
          opacity: 0.9;
          animation: fadeInUp 1s ease 0.2s both;
          font-weight: 400;
          font-style: italic;
          line-height: 1.6;
        }
        .hero p.hero-reviews {
          font-family: 'Nanum Gothic', sans-serif;
          font-size: 0.8rem;
          margin-top: 4rem;
          margin-bottom: 0;
          animation: fadeInUp 1s ease 0.3s both;
          color: rgb(0, 0, 0);
          letter-spacing: 0.5px;
          font-weight: 200;
          font-style: normal;
          opacity: 1;
        }
        .hero p.hero-trust {
          font-family: 'Nanum Gothic', sans-serif;
          font-size: 0.95rem;
          margin-top: 0.2rem;
          margin-bottom: 0;
          animation: fadeInUp 1s ease 0.4s both;
          color: rgb(0, 0, 0);
          letter-spacing: 0.3px;
          font-weight: 400;
          font-style: normal;
          opacity: 1;
          line-height: 1.5;
        }
        .btn-get-started {
          display: inline-block;
          margin-top: 6rem;
          padding: 0.5rem 2.3rem;
          background: #131c24;
          color: #ffffff;
          text-decoration: none;
          font-family: 'Nanum Gothic', sans-serif;
          font-size: 0.80rem;
          font-weight: 200;
          border-radius: 4px;
          border: 2px solid transparent;
          transition: all 0.3s ease;
          animation: fadeInUp 1s ease 0.5s both;
        }
        .btn-get-started:hover {
          background: #ffffff;
          color: #2c3e50;
          border-color: #2c3e50;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.15);
        }
        .hero p.hero-subline {
          font-family: 'Nanum Gothic', sans-serif;
          font-size: 0.75rem;
          margin-top: 1rem;
          margin-bottom: 0;
          animation: fadeInUp 1s ease 0.6s both;
          color: rgb(0, 0, 0);
          letter-spacing: 0.5px;
          font-weight: 200;
          font-style: normal;
          opacity: 1;
          line-height: 1.5;
        }
        section { padding: 5rem 2rem; }
        .container { max-width: 1200px; margin: 0 auto; }
        .section-header {
          text-align: center;
          margin-bottom: 3rem;
        }
        .section-header h2 {
          font-size: 2.5rem;
          color: #121312;
          margin-bottom: 0.5rem;
        }
        .section-header p {
          color: #6c757d;
          font-size: 1.1rem;
        }
        .divider {
          width: 60px;
          height: 4px;
          background: #3b80cf;
          margin: 1rem auto;
          border-radius: 2px;
        }
        .locations { background: #f8f9fa; }
        .locations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
        }
        .location-card {
          background: #ffffff;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          border-left: 4px solid #3b80cf;
        }
        .location-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }
        .location-card h3 {
          color: #121312;
          margin-bottom: 0.5rem;
          font-size: 1.3rem;
        }
        .location-card .city {
          color: #3b80cf;
          font-weight: 600;
          margin-bottom: 1rem;
          display: block;
        }
        .location-card p {
          color: #6c757d;
          line-height: 1.8;
          margin-bottom: 0.5rem;
        }
        .location-card a {
          color: #3b80cf;
          text-decoration: none;
          font-weight: 500;
        }
        .location-card a:hover {
          text-decoration: underline;
        }
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1rem;
        }
        .gallery-item {
          position: relative;
          overflow: hidden;
          border-radius: 10px;
          aspect-ratio: 1;
          cursor: pointer;
          background: #121312;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 3rem;
          transition: all 0.3s ease;
        }
        .gallery-item:hover {
          transform: scale(1.05);
        }
        .gallery-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0,0,0,0.8));
          padding: 1.5rem;
          color: white;
          transform: translateY(100%);
          transition: transform 0.3s;
        }
        .gallery-item:hover .gallery-overlay {
          transform: translateY(0);
        }
        .gallery-overlay h4 {
          font-size: 1.1rem;
          margin-bottom: 0.3rem;
        }
        .gallery-overlay p {
          font-size: 0.9rem;
          opacity: 0.9;
        }
        .testimonials { background: #f8f9fa; }
        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }
        .testimonial-card {
          background: #ffffff;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          position: relative;
        }
        .testimonial-card::before {
          content: '"';
          font-size: 4rem;
          color: #3b80cf;
          opacity: 0.3;
          position: absolute;
          top: 10px;
          left: 20px;
          line-height: 1;
        }
        .testimonial-text {
          margin-bottom: 1.5rem;
          font-style: italic;
          color: #555;
          line-height: 1.8;
        }
        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .author-avatar {
          width: 50px;
          height: 50px;
          background: #121312;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
        }
        .author-info h4 {
          color: #121312;
          margin-bottom: 0.2rem;
        }
        .author-info .stars {
          color: #3b80cf;
        }
        @media (max-width: 768px) {
          .hero h1 { font-size: 2.2rem; }
          .hero p { font-size: 1.1rem; }
          .locations-grid { grid-template-columns: 1fr; }
          section { padding: 3rem 1rem; }
        }
      `}</style>
    </>
  );
}
